import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FamilyLinkType } from '@bciti/types';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { OrganisationConfiguration } from 'src/app/global/global.model';
import { CardStatus, CardType } from '../../../user.model';

interface CardInfo {
    type: CardType;
    card: any;
}

@Component({
    selector: 'app-user-family-request-card',
    templateUrl: './user-family-request-card.component.html',
    styleUrls: ['./user-family-request-card.component.scss'],
})
export class UserFamilyRequestCardComponent implements OnInit, OnChanges {
    LinkType = FamilyLinkType;
    CardStatus = CardStatus;
    CardType = CardType;

    orgConfig: OrganisationConfiguration;
    userCards: CardInfo[] = [];
    displayAddress: any = null;
    isProfileAddress = false;
    dataReady = false;

    @Input() user: any;
    @Input() link: FamilyLinkType;
    @Input() familyApproved: boolean;
    @Input() editable = false;

    @Output() linkChanged = new EventEmitter<FamilyLinkType>();

    editing = false;
    familyLinkOptions = Object.values(FamilyLinkType);

    constructor(private store: Store<State>) {}

    get citizenProfileLink(): string {
        return this.user?._id ? `/user-update/${this.user._id}` : '';
    }

    ngOnInit(): void {
        this.dataReady = false;

        this.store.select(organisationConfigurationSelector).subscribe((config) => {
            this.orgConfig = config;
            this.processUserData();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.user) {
            this.dataReady = false;
            this.processUserData();
        }
    }

    private processUserData(): void {
        if (!this.user || !this.orgConfig) {
            return;
        }

        this.updateUserCards();
        this.determineAddressToDisplay();

        this.dataReady = true;
    }

    private updateUserCards(): void {
        if (this.user) {
            this.userCards = [
                { type: CardType.BCITI, card: this.user.bcitiCard },
                { type: CardType.LEGACY, card: this.user.legacyCard },
                { type: CardType.CITIZEN, card: this.user.citizenCard },
                { type: CardType.SPORTS, card: this.user.sportsCard },
                { type: CardType.LIBRARY, card: this.user.libraryCard },
            ].filter((cardInfo) => cardInfo.card);
        }
    }

    private determineAddressToDisplay(): void {
        if (!this.user) return;

        // Try citizen card first
        if (this.user.citizenCard && this.user.citizenCard.address) {
            this.displayAddress = this.user.citizenCard.address;
            this.isProfileAddress = false;
            return;
        }

        // Then BCITI card
        if (this.user.bcitiCard && this.user.bcitiCard.address) {
            this.displayAddress = this.user.bcitiCard.address;
            this.isProfileAddress = false;
            return;
        }

        // Then any other card with an address
        const cardsWithAddress = [this.user.legacyCard, this.user.sportsCard, this.user.libraryCard].filter((card) => card && card.address);

        if (cardsWithAddress.length > 0) {
            this.displayAddress = cardsWithAddress[0].address;
            this.isProfileAddress = false;
            return;
        }

        // If no card has an address, use the profile address
        if (this.user.addresses && this.user.addresses.length > 0) {
            this.displayAddress = this.user.addresses[0];
            this.isProfileAddress = true;
        } else {
            this.displayAddress = null;
            this.isProfileAddress = false;
        }
    }

    getCardIcon(cardType: string): string {
        const cardConfig = this.orgConfig?.support?.cards?.find((c) => c.type === cardType);
        return cardConfig?.identity?.icon || 'icon-id-card';
    }

    hasAnyCards(): boolean {
        return this.userCards.length > 0;
    }

    onLinkSelect(newLink: FamilyLinkType): void {
        this.link = newLink;
        this.editing = false;
        this.linkChanged.emit(newLink);
    }

    formatAddress(address: any): string {
        if (!address) return 'N/A';

        const number = address.number || '';
        const streetName = address.streetName || '';
        const city = address.city || '';
        const province = address.province || '';
        const postCode = address.postCode || '';

        const formattedAddress = `${number} ${streetName}, ${city}, ${province}, ${postCode}`.trim();
        return formattedAddress.replace(/^, /, '').replace(/, , /, ', ') || 'N/A';
    }
}
